interface LogEntry {
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  context?: Record<string, unknown>;
  timestamp: string;
  url: string;
}

const API_BASE = import.meta.env.VITE_API_URL ?? '';
const FLUSH_INTERVAL_MS = 5_000;
const MAX_BUFFER = 20;

const _buffer: LogEntry[] = [];
let _flushTimer: ReturnType<typeof setTimeout> | null = null;

async function _flush(): Promise<void> {
  if (_buffer.length === 0) return;

  const token = localStorage.getItem('auth_token');
  if (!token) return; // only send when authenticated

  const batch = _buffer.splice(0, _buffer.length);

  try {
    await fetch(`${API_BASE}/v1/logs/frontend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ logs: batch }),
    });
  } catch {
    // Never let logging break the app — silently discard on network error
  }
}

function _scheduleFlush(): void {
  if (_flushTimer !== null) return;
  _flushTimer = setTimeout(() => {
    _flushTimer = null;
    _flush();
  }, FLUSH_INTERVAL_MS);
}

function _enqueue(entry: LogEntry): void {
  _buffer.push(entry);
  if (entry.level === 'error' || _buffer.length >= MAX_BUFFER) {
    if (_flushTimer !== null) {
      clearTimeout(_flushTimer);
      _flushTimer = null;
    }
    _flush();
  } else {
    _scheduleFlush();
  }
}

function _makeEntry(
  level: LogEntry['level'],
  message: string,
  context?: unknown,
): LogEntry {
  let safeContext: Record<string, unknown> | undefined;
  if (context !== undefined && context !== null && context !== '') {
    safeContext =
      typeof context === 'object'
        ? (context as Record<string, unknown>)
        : { value: context };
  }
  return {
    level,
    message,
    context: safeContext,
    timestamp: new Date().toISOString(),
    url: typeof window !== 'undefined' ? window.location.pathname : '',
  };
}

export const logger = {
  info: (message: string, context?: unknown) => {
    console.log(`[INFO] ${message}`, context ?? '');
    _enqueue(_makeEntry('info', message, context));
  },

  error: (message: string, error?: unknown, context?: unknown) => {
    console.error(`[ERROR] ${message}`, error ?? '', context ?? '');
    const merged: Record<string, unknown> = {};
    if (error !== undefined && error !== null) {
      if (error instanceof Error) {
        merged.error = error.message;
        if (error.stack) merged.stack = error.stack;
      } else if (typeof error === 'string') {
        merged.error = error;
      } else {
        try {
          merged.error = JSON.stringify(error);
        } catch {
          merged.error = String(error);
        }
      }
    }
    if (context !== undefined && context !== null && context !== '') {
      Object.assign(merged, typeof context === 'object' ? context : { context });
    }
    _enqueue(_makeEntry('error', message, Object.keys(merged).length ? merged : undefined));
  },

  warn: (message: string, context?: unknown) => {
    console.warn(`[WARN] ${message}`, context ?? '');
    _enqueue(_makeEntry('warn', message, context));
  },

  debug: (message: string, context?: unknown) => {
    if (import.meta.env.DEV) {
      console.debug(`[DEBUG] ${message}`, context ?? '');
    }
    _enqueue(_makeEntry('debug', message, context));
  },
};
